// UserMenuPopover.tsx — Nutzer-Menü der Navigation (SP2 der Profil-Seite).
// Portal-Popover im REFINED-Stil mit den Items „Profil anzeigen" (→ /profil)
// und „Abmelden" (dazwischen „Was ist neu?", 01.10.2026). Render-Prop-Trigger (mal Circle, mal ⋮-Button) + children-
// loses Item-Rendering. Schließt bei Außenklick / Esc / Scroll-Reposition.
//
// WICHTIG (Tagesabschluss-Gate, Konsistenz P10 / R3): `onLogout` ist Pflicht-
// Prop OHNE Default. Der Aufrufer MUSS `requestLogout` durchreichen — niemals
// `signOut` direkt. So bleibt das Abmelde-Gate erhalten, egal welcher Trigger
// (Topbar-Circle oder Sidebar-⋮) den Logout auslöst.
//
// Eigenes Portal-Popover statt `RdPopover`, weil (1) RdPopover ausschließlich
// nach UNTEN öffnet (`top = trigger.bottom + 4`) und im Sidebar-Footer am
// unteren Viewport-Rand abgeschnitten würde, und (2) die `.rd-pop-*`-CSS-Klassen
// an `contactRedesign.css` hängen (ggf. nicht im NavigationLayout-Scope geladen).
// Inline-Styles (REFINED-Tokens) → keine CSS-Scope-Abhängigkeit.

import React from "react";
import { createPortal } from "./windowManager/browserFenster/fensterPortal";
import { useNavigate } from "react-router";
import { REFINED } from "./risikoanalyseStep/tokens";
import { oeffneWasIstNeu } from "./update/wasIstNeuStand";
import { alleFenster } from "./windowManager/browserFenster/fensterRegister";

export interface UserMenuPopoverProps {
  /** Render-Prop für den Trigger (Circle oder ⋮-Button). Bekommt toggle/open/ref. */
  trigger: (p: {
    open: boolean;
    toggle: () => void;
    ref: React.Ref<HTMLButtonElement>;
  }) => React.ReactNode;
  /** "Profil anzeigen" sichtbar? (Desktop-Sidebar-⋮: false → nur Abmelden). */
  showProfile?: boolean; // Default true
  /** "Abmelden" sichtbar? (praktisch immer true). */
  showLogout?: boolean; // Default true
  /** "Was ist neu?" sichtbar? Öffnet das Änderungs-Popup auch ohne neue Version. Default true. */
  showWasIstNeu?: boolean;
  /** Tagesabschluss-Gate. MUSS requestLogout sein (NICHT signOut). Pflicht. */
  onLogout: () => void | Promise<void>;
  /** Navigation zum Profil; Default navigate('/profil'). */
  onProfile?: () => void;
  /** Horizontale Ausrichtung relativ zum Trigger. Default 'end'. */
  align?: "start" | "end";
  /** Popover-Breite (px). Default 200. */
  width?: number;
}

const MENU_GAP = 6;

export const UserMenuPopover: React.FC<UserMenuPopoverProps> = ({
  trigger,
  showProfile = true,
  showLogout = true,
  showWasIstNeu = true,
  onLogout,
  onProfile,
  align = "end",
  width = 200,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{
    top: number;
    left: number;
    placement: "down" | "up";
  } | null>(null);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const popRef = React.useRef<HTMLDivElement>(null);

  // Anzahl sichtbarer Items → grobe Höhenschätzung für die Auf-/Ab-Entscheidung.
  const itemCount = (showProfile ? 1 : 0) + (showWasIstNeu ? 1 : 0) + (showLogout ? 1 : 0);
  const estHeight = itemCount * 46 + 16; // ~44px Item + Padding

  const place = React.useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let left = align === "end" ? r.right - width : r.left;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));

    // Nach unten öffnen, außer es passt unten nicht aber oben schon
    // (Sidebar-Footer am unteren Rand → nach OBEN ausklappen).
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const openUp = spaceBelow < estHeight + MENU_GAP && spaceAbove > spaceBelow;

    const top = openUp
      ? Math.max(8, r.top - estHeight - MENU_GAP)
      : Math.min(r.bottom + MENU_GAP, window.innerHeight - estHeight - 8);

    setPos({ top, left, placement: openUp ? "up" : "down" });
  }, [align, width, estHeight]);

  const toggle = React.useCallback(() => {
    setOpen((o) => {
      if (!o) place();
      return !o;
    });
  }, [place]);
  const close = React.useCallback(() => setOpen(false), []);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    alleFenster.addEventListener("pointerdown", onDown);
    alleFenster.addEventListener("keydown", onKey);
    alleFenster.addEventListener("resize", place);
    alleFenster.addEventListener("scroll", place, true); // capture: jeder Ancestor-Scroller
    return () => {
      alleFenster.removeEventListener("pointerdown", onDown);
      alleFenster.removeEventListener("keydown", onKey);
      alleFenster.removeEventListener("resize", place);
      alleFenster.removeEventListener("scroll", place, true);
    };
  }, [open, close, place]);

  const goProfile = () => {
    close();
    if (onProfile) onProfile();
    else navigate("/profil");
  };

  const goWasIstNeu = () => {
    close();
    oeffneWasIstNeu();
  };

  const doLogout = () => {
    close();
    void onLogout();
  };

  const itemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    minHeight: 44,
    textAlign: "left",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    font: `600 14px/1.2 ${REFINED.fontUI}`,
    color: REFINED.ink,
    padding: "0 12px",
    borderRadius: 8,
    transition: "background .12s, color .12s",
  };
  const onHover = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = REFINED.aub50;
    e.currentTarget.style.color = REFINED.aub;
  };
  const offHover = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = "transparent";
    e.currentTarget.style.color = REFINED.ink;
  };

  return (
    <>
      {trigger({ open, toggle, ref: btnRef })}
      {open && pos
        ? createPortal(
            <div
              ref={popRef}
              role="menu"
              style={{
                position: "fixed",
                top: pos.top,
                left: pos.left,
                width,
                zIndex: 2000,
                background: REFINED.white,
                border: `1px solid #ece6ec`,
                borderRadius: 12,
                boxShadow:
                  "0 18px 46px -16px rgba(44,10,38,.30), 0 2px 10px rgba(44,10,38,.08)",
                overflow: "hidden",
                fontFamily: REFINED.fontUI,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: 6 }}>
                {showProfile && (
                  <button
                    type="button"
                    role="menuitem"
                    style={itemStyle}
                    onMouseEnter={onHover}
                    onMouseLeave={offHover}
                    onClick={goProfile}
                  >
                    Profil anzeigen
                  </button>
                )}
                {showWasIstNeu && (
                  <button
                    type="button"
                    role="menuitem"
                    style={itemStyle}
                    onMouseEnter={onHover}
                    onMouseLeave={offHover}
                    onClick={goWasIstNeu}
                  >
                    Was ist neu?
                  </button>
                )}
                {(showProfile || showWasIstNeu) && showLogout && (
                  <div
                    style={{
                      height: 1,
                      margin: "4px 8px",
                      background: "#ece6ec",
                    }}
                  />
                )}
                {showLogout && (
                  <button
                    type="button"
                    role="menuitem"
                    style={itemStyle}
                    onMouseEnter={onHover}
                    onMouseLeave={offHover}
                    onClick={doLogout}
                  >
                    Abmelden
                  </button>
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export default UserMenuPopover;
